import { Button, ResponsiveTable, Spinner, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';

import { useMoneyFormatters } from '../_money/useMoneyFormatters.js';
import { usePayrollHistoryQuery } from './usePayrollQuery.js';

/**
 * The `/payroll/history` route — the locked-run list for the tenant. Each row is a committed,
 * immutable pay run (week, total gross, employee count, locked-at, locked-by) linking to the Run
 * Detail. READ-ONLY; the server returns the runs tenant-scoped (SEC-11). Money numerals are font-mono.
 */
export function PayrollHistoryRoute() {
  const { t } = useTranslation();
  const { formatCurrency } = useMoneyFormatters();
  const query = usePayrollHistoryQuery();

  if (query.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  if (query.isError) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('payroll:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('payroll:error.body')}</p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm ring-offset-background transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t('payroll:error.retry')}
        </button>
      </section>
    );
  }

  const runs = query.data.runs ?? [];

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-3xl text-foreground">
            {t('payroll:history.title')}
          </h1>
          <p className="mt-1 max-w-2xl text-muted-foreground text-sm">
            {t('payroll:history.subtitle')}
          </p>
        </div>
        <Button asChild variant="secondary">
          <Link to="/payroll">{t('payroll:history.backToCenter')}</Link>
        </Button>
      </header>

      {runs.length > 0 ? (
        <ResponsiveTable className="rounded-md border border-border bg-card">
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">{t('payroll:history.caption')}</caption>
            <thead>
              <tr className="border-border border-b text-left text-muted-foreground">
                <th scope="col" className="px-4 py-3 font-medium">
                  {t('payroll:history.period')}
                </th>
                <th scope="col" className="px-4 py-3 text-right font-medium">
                  {t('payroll:history.employees')}
                </th>
                <th scope="col" className="px-4 py-3 text-right font-medium">
                  {t('payroll:history.totalGross')}
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  {t('payroll:history.lockedAt')}
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  <span className="sr-only">{t('payroll:history.viewLabel')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.id} className="border-border/60 border-b last:border-b-0">
                  <td className="px-4 py-3 font-mono text-foreground">
                    {run.weekStart} – {run.weekEnd}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-foreground">
                    {run.employeeCount}
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-foreground">
                    {formatCurrency(run.totalGrossPay)}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{run.lockedAt}</td>
                  {/* The header for this column is sr-only, so the card view would otherwise
                      print that screen-reader sentence beside the link. */}
                  <td className="px-4 py-3 text-right" data-label="">
                    <Link
                      to="/payroll/runs/$id"
                      params={{ id: String(run.id) }}
                      className="font-medium text-primary-ink underline underline-offset-2"
                    >
                      {t('payroll:history.view')}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ResponsiveTable>
      ) : (
        <div className="rounded-md border border-border bg-card p-16 text-center">
          <h2 className="font-display font-semibold text-foreground text-xl">
            {t('payroll:history.emptyHeading')}
          </h2>
          <p className="mx-auto mt-2 max-w-sm text-muted-foreground text-sm">
            {t('payroll:history.emptyBody')}
          </p>
        </div>
      )}
    </div>
  );
}
